import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Loader2, FileText, Search, Globe, Coins } from "lucide-react";
import { useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { tasksApi, serpApi, projectsApi, adminApi, type Project, type AnalysisAccountProfile, type SerpSettingItem } from "@/api";
import { LOCATION_OPTIONS, LANGUAGE_OPTIONS, DEFAULT_LOCATION, DEFAULT_LANGUAGE } from "@/lib/serp-constants";

interface NewAnalysisModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function NewAnalysisModal({ open: controlledOpen, onOpenChange }: NewAnalysisModalProps = {}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const queryClient = useQueryClient();

  // Shared
  const [projectId, setProjectId] = useState("none");
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectAccount, setNewProjectAccount] = useState("__system_default__");

  // Content - single
  const [url, setUrl] = useState("");
  const [contentKeyword, setContentKeyword] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [contentPriority, setContentPriority] = useState("normal");

  // Content - batch
  const [batchUrls, setBatchUrls] = useState("");
  const [batchContentKeyword, setBatchContentKeyword] = useState("");
  const [batchPriority, setBatchPriority] = useState("normal");

  // SERP - single
  const [serpKeyword, setSerpKeyword] = useState("");
  const [serpLocation, setSerpLocation] = useState(DEFAULT_LOCATION.toString());
  const [serpLanguage, setSerpLanguage] = useState(DEFAULT_LANGUAGE);
  const [serpTargetUrl, setSerpTargetUrl] = useState("");
  const [serpProductService, setSerpProductService] = useState("");

  // SERP - batch
  const [batchKeywords, setBatchKeywords] = useState("");
  const [batchLocation, setBatchLocation] = useState(DEFAULT_LOCATION.toString());
  const [batchLanguage, setBatchLanguage] = useState(DEFAULT_LANGUAGE);
  const [batchTargetUrl, setBatchTargetUrl] = useState("");
  const [batchProductService, setBatchProductService] = useState("");

  // Pipeline stages (shared across all tabs)
  const [selectedStages, setSelectedStages] = useState<Set<string>>(new Set());
  const [tag, setTag] = useState("");

  // Auto-detect tag from URL
  const autoDetectTag = useCallback((inputUrl: string): string => {
    try {
      const hostname = new URL(inputUrl).hostname.toLowerCase();
      if (hostname.includes('alibaba.com')) return 'alibaba';
      if (hostname.includes('made-in-china.com')) return 'mic';
      if (hostname.includes('globalsources.com')) return 'gs';
      if (hostname.includes('1688.com')) return '1688';
    } catch { /* ignore */ }
    return '';
  }, []);

  // Check if URL needs BrowserAct
  const needsBrowserAct = useCallback((inputUrl: string): boolean => {
    try {
      const hostname = new URL(inputUrl).hostname.toLowerCase();
      return hostname.includes('alibaba.com') || hostname.includes('1688.com');
    } catch { return false; }
  }, []);

  // Toggle a pipeline stage
  const toggleStage = (stage: string) => {
    setSelectedStages(prev => {
      const next = new Set(prev);
      if (next.has(stage)) next.delete(stage);
      else next.add(stage);
      return next;
    });
  };

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ["projects"],
    queryFn: async () => { const r = await projectsApi.list(); return r.data || []; },
  });

  const { data: serpStatus } = useQuery({
    queryKey: ["serp-status"],
    queryFn: async () => { const r = await serpApi.status(); return r.data; },
    enabled: open,
  });

  const { data: accountProfiles = [] } = useQuery<AnalysisAccountProfile[]>({
    queryKey: ["analysis-account-profiles"],
    queryFn: async () => (await projectsApi.analysisAccountProfiles()).data || [],
    enabled: open,
  });

  const selectedProject = projects.find(project => project.project_id === projectId);
  const selectedProfile = accountProfiles.find(profile => selectedProject?.analysis_account_profile_key ? profile.accountKey === selectedProject.analysis_account_profile_key : profile.isDefault === true);
  const accountLabel = selectedProject ? (selectedProfile ? (selectedProfile.accountKey === "company" ? "Company" : "Personal") : "Unavailable") : (selectedProfile ? "System Default" : "Unavailable");
  const llmLabel = selectedProfile?.llmProfileKey === "cometapi_company" ? "CometAPI" : selectedProfile?.llmProfileKey === "openai_personal" ? "OpenAI" : "Unavailable";
  const analysisReady = selectedProfile?.dataforseoConfigured === true && selectedProfile.llmConfigured === true;

  // Fetch SERP settings for cost estimation
  const { data: serpSettingsRaw } = useQuery<SerpSettingItem[]>({
    queryKey: ["serp-settings"],
    queryFn: async () => { const r = await adminApi.getSerpSettings(); return r.data || []; },
    enabled: open,
    staleTime: 60000,
  });

  const costEstimate = useMemo(() => {
    if (!serpSettingsRaw) return null;
    const map = new Map(serpSettingsRaw.map(s => [s.setting_key, s.setting_value]));
    const depth = Number(map.get("default_depth")) || 100;
    const rectangles = map.get("calculate_rectangles") === true || map.get("calculate_rectangles") === "true";
    const aiOverview = map.get("load_async_ai_overview") === true || map.get("load_async_ai_overview") === "true";
    const baseCost = 0.002;
    const depthMul = depth / 10;
    const rectMul = rectangles ? 2 : 1;
    const aiExtra = aiOverview ? 0.0006 : 0;
    const perQuery = baseCost * depthMul * rectMul + aiExtra;
    return { perQuery, depth, rectangles, aiOverview };
  }, [serpSettingsRaw]);

  // Create project inline
  const createProjectMutation = useMutation({
    mutationFn: () => projectsApi.create({
      name: newProjectName.trim(),
      analysis_account_profile_key: newProjectAccount === "__system_default__" ? null : newProjectAccount as "personal" | "company",
    }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      const newId = res.data?.project_id;
      if (newId) setProjectId(newId);
      toast.success(`專案「${newProjectName.trim()}」已建立`);
      setNewProjectName("");
      setNewProjectAccount("__system_default__");
      setShowNewProject(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Helper: get pipeline stage data for API calls
  const stageData = () => ({
    tag: tag.trim() || undefined,
    selected_stages: selectedStages.size > 0 ? [...selectedStages] : undefined,
  });

  // Content mutations
  const createTaskMutation = useMutation({
    mutationFn: (data: { url: string; keyword?: string; project_id?: string; priority?: number; webhook_url?: string; tag?: string; selected_stages?: string[] }) =>
      tasksApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      toast.success("任務已提交");
      setUrl(""); setContentKeyword(""); setWebhookUrl("");
      setOpen(false);
    },
    onError: (err: Error) => toast.error(`提交失敗: ${err.message}`),
  });

  const batchCreateMutation = useMutation({
    mutationFn: (urls: string[]) =>
      tasksApi.createBatch({
        urls,
        keyword: batchContentKeyword.trim() || undefined,
        project_id: projectId !== "none" ? projectId : undefined,
        priority: batchPriority === "high" ? 1 : undefined,
        ...stageData(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      toast.success("批次任務已提交");
      setBatchUrls(""); setBatchContentKeyword("");
      setOpen(false);
    },
    onError: (err: Error) => toast.error(`批次提交失敗: ${err.message}`),
  });

  // SERP mutations
  const serpMutation = useMutation({
    mutationFn: () =>
      serpApi.analyze({
        keyword: serpKeyword.trim(),
        location_code: parseInt(serpLocation),
        language_code: serpLanguage,
        project_id: projectId !== "none" ? projectId : undefined,
        target_url: serpTargetUrl.trim() || undefined,
        auto_scrape: true,
        ...stageData(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serp-requests"] });
      queryClient.invalidateQueries({ queryKey: ["serp-stats"] });
      toast.success(`SERP 分析已提交：${serpKeyword}`);
      setSerpKeyword(""); setSerpTargetUrl(""); setSerpProductService("");
      setOpen(false);
    },
    onError: (err: Error) => toast.error(`分析失敗: ${err.message}`),
  });

  const batchSerpMutation = useMutation({
    mutationFn: (keywords: string[]) =>
      serpApi.batchAnalyze({
        keywords,
        location_code: parseInt(batchLocation),
        language_code: batchLanguage,
        project_id: projectId !== "none" ? projectId : undefined,
        target_url: batchTargetUrl.trim() || undefined,
        ...stageData(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serp-requests"] });
      queryClient.invalidateQueries({ queryKey: ["serp-stats"] });
      toast.success("批次 SERP 分析已提交");
      setBatchKeywords(""); setBatchTargetUrl(""); setBatchProductService("");
      setOpen(false);
    },
    onError: (err: Error) => toast.error(`批次分析失敗: ${err.message}`),
  });

  const pid = () => projectId !== "none" ? projectId : undefined;

  const handleSubmitSingle = () => {
    if (!url.trim()) { toast.error("請輸入 URL"); return; }
    const detectedTag = tag.trim() || autoDetectTag(url.trim());
    createTaskMutation.mutate({
      url: url.trim(),
      keyword: contentKeyword.trim() || undefined,
      project_id: pid(),
      priority: contentPriority === "high" ? 1 : undefined,
      webhook_url: webhookUrl.trim() || undefined,
      tag: detectedTag || undefined,
      selected_stages: selectedStages.size > 0 ? [...selectedStages] : undefined,
    });
  };

  const handleSubmitBatch = () => {
    const urls = batchUrls.split("\n").map(u => u.trim()).filter(u => u);
    if (urls.length === 0) { toast.error("請輸入至少一個 URL"); return; }
    if (urls.length > 200) { toast.error("最多 200 個 URL"); return; }
    batchCreateMutation.mutate(urls);
  };

  const handleSubmitKeyword = () => {
    if (!serpKeyword.trim()) { toast.error("請輸入關鍵字"); return; }
    serpMutation.mutate();
  };

  const handleSubmitBatchKeywords = () => {
    const kws = batchKeywords.split("\n").map(k => k.trim()).filter(k => k);
    if (kws.length === 0) { toast.error("請輸入至少一個關鍵字"); return; }
    if (kws.length > 200) { toast.error("最多 200 個關鍵字"); return; }
    batchSerpMutation.mutate(kws);
  };

  const urlCount = batchUrls.split("\n").filter(u => u.trim()).length;
  const kwCount = batchKeywords.split("\n").filter(k => k.trim()).length;

  // Project selector component (shared across tabs)
  const ProjectSelector = () => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>專案</Label>
        <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs"
          onClick={() => setShowNewProject(!showNewProject)}>
          {showNewProject ? "選擇現有" : "+ 建立新專案"}
        </Button>
      </div>
      {showNewProject ? (
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input placeholder="新專案名稱" value={newProjectName} onChange={e => setNewProjectName(e.target.value)} />
            <Button size="sm" disabled={!newProjectName.trim() || createProjectMutation.isPending}
              onClick={() => createProjectMutation.mutate()}>
              {createProjectMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : "建立"}
            </Button>
          </div>
          <Select value={newProjectAccount} onValueChange={setNewProjectAccount}>
            <SelectTrigger><SelectValue placeholder="分析帳戶" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__system_default__">系統預設</SelectItem>
              {accountProfiles.map(profile => (
                <SelectItem key={profile.accountKey} value={profile.accountKey} disabled={!profile.dataforseoConfigured || !profile.llmConfigured}>
                  {profile.accountKey === "personal" ? "Personal（DataForSEO + OpenAI）" : "Company（DataForSEO + CometAPI）"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger><SelectValue placeholder="選擇專案（選填）" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">無專案</SelectItem>
            {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
    </div>
  );

  // Pipeline stage selector (shared across tabs)
  const PipelineStagesSelector = ({ currentUrl }: { currentUrl?: string }) => {
    const showBrowserActHint = currentUrl ? needsBrowserAct(currentUrl) : false;
    return (
      <div className="space-y-2 border-t pt-3 mt-3">
        <Label className="text-muted-foreground text-xs">Pipeline Stages</Label>
        <div className="grid grid-cols-2 gap-1.5">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Checkbox checked disabled />
            HTML Fetch
          </label>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <Checkbox checked disabled />
            HTML Parse
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={selectedStages.has('browseract')}
              onCheckedChange={() => toggleStage('browseract')}
            />
            BrowserAct Screenshot
            {showBrowserActHint && <Badge variant="outline" className="text-[10px] px-1 py-0 border-orange-400 text-orange-500">Recommended</Badge>}
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={selectedStages.has('rag_vectorize')}
              onCheckedChange={() => toggleStage('rag_vectorize')}
            />
            RAG Vectorize
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={selectedStages.has('seo_scoring')}
              onCheckedChange={() => toggleStage('seo_scoring')}
            />
            SEO Scoring
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={selectedStages.has('ncb_backup')}
              onCheckedChange={() => toggleStage('ncb_backup')}
            />
            NCB Backup
          </label>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Tag</Label>
          <Input
            placeholder={currentUrl ? (autoDetectTag(currentUrl) || 'general') : 'Auto-detect from URL'}
            value={tag}
            onChange={e => setTag(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {controlledOpen === undefined && (
        <DialogTrigger asChild>
          <Button><Plus className="h-4 w-4 mr-2" /> 新增分析</Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />新增 SEO 分析
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            網頁解析（手動貼網址批量爬取）或關鍵字搜尋（DataForSEO SERP 排名）
          </p>
        </DialogHeader>

        <Tabs defaultValue="keyword">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="content" className="flex items-center gap-1.5">
              <FileText className="h-3.5 w-3.5" />網頁解析
            </TabsTrigger>
            <TabsTrigger value="keyword" className="flex items-center gap-1.5">
              <Search className="h-3.5 w-3.5" />關鍵字搜尋
            </TabsTrigger>
          </TabsList>

          {/* ────── Content Analysis ────── */}
          <TabsContent value="content">
            {/* DataForSEO status not needed here */}
            <Tabs defaultValue="single">
              <TabsList className="grid w-full grid-cols-2 mt-2">
                <TabsTrigger value="single">單一網址</TabsTrigger>
                <TabsTrigger value="batch">批次輸入</TabsTrigger>
              </TabsList>

              <TabsContent value="single" className="space-y-4 pt-3">
                <ProjectSelector />
                <div className="space-y-2">
                  <Label>優先級</Label>
                  <Select value={contentPriority} onValueChange={setContentPriority}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">一般 — 加入佇列依序處理</SelectItem>
                      <SelectItem value="high">高優先 — 優先處理</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>網址 *</Label>
                  <Input placeholder="https://example.com/page" value={url} onChange={e => setUrl(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>目標關鍵字（選填）</Label>
                  <Input placeholder="相關關鍵字" value={contentKeyword} onChange={e => setContentKeyword(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Webhook 回呼網址（選填）</Label>
                  <Input placeholder="https://your-webhook.com/callback" value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} />
                </div>
                <PipelineStagesSelector currentUrl={url} />
                <Button onClick={handleSubmitSingle} className="w-full" disabled={createTaskMutation.isPending}>
                  {createTaskMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />提交中...</> : "開始分析"}
                </Button>
              </TabsContent>

              <TabsContent value="batch" className="space-y-4 pt-3">
                <ProjectSelector />
                <div className="space-y-2">
                  <Label>優先級</Label>
                  <Select value={batchPriority} onValueChange={setBatchPriority}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="normal">一般 — 加入佇列依序處理</SelectItem>
                      <SelectItem value="high">高優先 — 優先處理</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>網址清單 *</Label>
                    <span className="text-xs text-muted-foreground">{urlCount} 個有效網址（最多 200 個）</span>
                  </div>
                  <Textarea rows={6} className="font-mono text-sm"
                    placeholder={"每行一個網址，例如：\nhttps://example.com/page1\nhttps://example.com/page2\nhttps://example.com/page3"}
                    value={batchUrls} onChange={e => setBatchUrls(e.target.value)} />
                  <p className="text-xs text-muted-foreground">每行輸入一個網址，系統會自動過濾無效網址</p>
                </div>
                <div className="space-y-2">
                  <Label>目標關鍵字（選填）（共用）</Label>
                  <Input placeholder="例如：SEO 優化" value={batchContentKeyword} onChange={e => setBatchContentKeyword(e.target.value)} />
                </div>
                <PipelineStagesSelector />
                <Button onClick={handleSubmitBatch} className="w-full" disabled={batchCreateMutation.isPending}>
                  {batchCreateMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />提交中...</> : `批次分析 (${urlCount} 個)`}
                </Button>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* ────── Keyword Search (SERP) ────── */}
          <TabsContent value="keyword">
            <div className="mt-2 mb-3 flex flex-wrap items-center gap-2 text-xs">
              <Badge variant={analysisReady ? "default" : "destructive"}>{accountLabel}</Badge>
              <span className="text-muted-foreground">DataForSEO: {selectedProfile?.dataforseoConfigured ? "已就緒" : "不可用"}</span>
              <span className="text-muted-foreground">LLM: {selectedProfile?.llmConfigured ? `${llmLabel} 已就緒` : `${llmLabel} 不可用`}</span>
            </div>

            <Tabs defaultValue="single-kw">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="single-kw">單一分析</TabsTrigger>
                <TabsTrigger value="batch-kw">批量任務</TabsTrigger>
              </TabsList>

              <TabsContent value="single-kw" className="space-y-4 pt-3">
                <div className="space-y-2">
                  <Label>關鍵字 *</Label>
                  <Input placeholder="輸入要分析的關鍵字" value={serpKeyword}
                    onChange={e => setSerpKeyword(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && handleSubmitKeyword()} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>地區</Label>
                    <Select value={serpLocation} onValueChange={setSerpLocation}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LOCATION_OPTIONS.map(o => <SelectItem key={o.value} value={o.value.toString()}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>語系</Label>
                    <Select value={serpLanguage} onValueChange={setSerpLanguage}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LANGUAGE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <ProjectSelector />
                <div className="space-y-2">
                  <Label>目標網址（選填）</Label>
                  <Input placeholder="https://your-website.com" value={serpTargetUrl} onChange={e => setSerpTargetUrl(e.target.value)} />
                  <p className="text-xs text-muted-foreground">追蹤此網址在各關鍵字的排名</p>
                </div>
                <div className="space-y-2">
                  <Label>產品/服務分類（選填）</Label>
                  <Input placeholder="例如：SEO 服務、電商平台" value={serpProductService} onChange={e => setSerpProductService(e.target.value)} />
                </div>
                <PipelineStagesSelector currentUrl={serpTargetUrl} />
                {costEstimate && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
                    <Coins className="h-3.5 w-3.5 shrink-0" />
                    <span>
                      Est. {costEstimate.perQuery.toFixed(4)} Credits/query (depth {costEstimate.depth}
                      {costEstimate.rectangles ? ", rectangles" : ""}
                      {costEstimate.aiOverview ? ", AI Overview" : ""})
                    </span>
                  </div>
                )}
                <Button onClick={handleSubmitKeyword} className="w-full"
                  disabled={serpMutation.isPending || !analysisReady}>
                  {serpMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />處理中...</> : "搜尋分析"}
                </Button>
              </TabsContent>

              <TabsContent value="batch-kw" className="space-y-4 pt-3">
                <ProjectSelector />
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>關鍵字清單 *</Label>
                    <span className="text-xs text-muted-foreground">{kwCount} 個關鍵字（最多 200 個）</span>
                  </div>
                  <Textarea rows={6} className="font-mono text-sm"
                    placeholder={"每行一個關鍵字，例如：\nSEO 優化\n網站排名\n關鍵字分析"}
                    value={batchKeywords} onChange={e => setBatchKeywords(e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>地區</Label>
                    <Select value={batchLocation} onValueChange={setBatchLocation}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LOCATION_OPTIONS.map(o => <SelectItem key={o.value} value={o.value.toString()}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>語系</Label>
                    <Select value={batchLanguage} onValueChange={setBatchLanguage}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {LANGUAGE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>目標網址（選填）（共用）</Label>
                  <Input placeholder="https://your-website.com" value={batchTargetUrl} onChange={e => setBatchTargetUrl(e.target.value)} />
                  <p className="text-xs text-muted-foreground">所有關鍵字將使用相同的目標網址進行 SEO 分析</p>
                </div>
                <div className="space-y-2">
                  <Label>產品/服務分類（選填）（共用）</Label>
                  <Input placeholder="例如：SEO 服務、電商平台" value={batchProductService} onChange={e => setBatchProductService(e.target.value)} />
                </div>
                <PipelineStagesSelector currentUrl={batchTargetUrl} />
                {costEstimate && kwCount > 0 && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
                    <Coins className="h-3.5 w-3.5 shrink-0" />
                    <span>
                      Est. {(costEstimate.perQuery * kwCount).toFixed(4)} Credits total ({kwCount} x {costEstimate.perQuery.toFixed(4)})
                    </span>
                  </div>
                )}
                <Button onClick={handleSubmitBatchKeywords} className="w-full"
                  disabled={batchSerpMutation.isPending || !analysisReady}>
                  {batchSerpMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />處理中...</> : `開始批量分析 (${kwCount} 個)`}
                </Button>
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
